import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, Label } from 'recharts';
import { Calendar, Bell, Clock, ChevronDown } from 'lucide-react'; 
import { Card, Row, Col } from 'react-bootstrap';
import { attendanceData, stats, genderData, totalValue, upcomingEvents, gradeDistribution, COLORS, assignments } from '../../data/dashboard';
import { AuthContext } from '../../contexts/AuthContext';
import ModalForm from '../../Components/ModalForm';
import { changePassword } from '../../Services/authService';
import Swal from 'sweetalert2';

const AdminDashboard = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const auth = useContext(AuthContext);

    const handleOptionClick = (option) => {
        if (option === 'Logout') {
            auth.logout();
        }
    };

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    const validateData = async (formData) => {
        const { newPassword, confirmNewPassword } = formData;
        if (newPassword !== confirmNewPassword) {
            Swal.fire('New password and Confirm new password must be same.');
            return false;
        }
        return true;
    };

    const handleFormSubmit = async (data) => {
        if (validateData(data)) {
            await changePassword(data, auth.user);
        }
    };

    return (
        <>
            <Helmet>
                <title>Admin Dashboard | Admin Portal Oasis Educational Institute</title>
                <meta name="description" content="This is the Admin Portal Page of Oasis Educational Institute" />
            </Helmet>
            <div className="p-3">
                {/* Header */}
                <header className="bg-white-100 rounded-lg shadow-lg p-4 mb-8">
                    <div className="flex flex-col sm:flex-row items-center justify-between">
                        <div className="mb-4 sm:mb-0">
                            <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
                            <p className="text-gray-600">Welcome back 👋</p>
                        </div>
                        <div className="flex items-center space-x-4">
                            <Bell className="w-6 h-6 text-gray-600 cursor-pointer hover:text-blue-500" />
                            <div className="relative">
                                <div
                                    className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold cursor-pointer"
                                    onClick={() => setIsOpen(!isOpen)}
                                >
                                    A
                                    <ChevronDown className="w-4 h-4 ml-1" /> {/* Added Chevron */}
                                </div>

                                {/* Dropdown Menu */}
                                {isOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-2 z-50">
                                        <div
                                            className="px-4 py-2 text-gray-700 hover:bg-gray-100 cursor-pointer"
                                            onClick={handleOpenModal}
                                        >
                                            Change Password
                                        </div>
                                        <div
                                            className="px-4 py-2 text-gray-700 hover:bg-gray-100 cursor-pointer"
                                            onClick={()=>{}}
                                        >
                                            Edit Dashboard
                                        </div>
                                        <div
                                            className="px-4 py-2 text-gray-700 hover:bg-gray-100 cursor-pointer"
                                            
                                        >
                                            <Link to="/register">Add new user</Link> 
                                        </div>
                                        <div
                                            className="px-4 py-2 text-gray-700 hover:bg-gray-100 cursor-pointer"
                                            onClick={() => handleOptionClick('Logout')}
                                        >
                                            Logout
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Stats Cards */}
                <Row className="g-4 mb-4">
                    {stats.map((stat, index) => (
                        <Col key={index} xs={12} sm={6} md={3}>
                            <Card style={{ backgroundColor: stat.color }} className="text-white h-100 shadow-lg">
                                <Card.Body className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="mb-1">{stat.title}</p>
                                        <h3 className="fw-bold mb-0">{stat.value}</h3>
                                    </div>
                                    <stat.icon size={32} />
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Charts Row */}
                <Row className="g-4 mb-4">
                    <Col xs={12} md={6}>
                        <Card className="h-100 shadow-lg">
                            <Card.Body>
                                <h2 className="text-md font-bold mb-4">Total Students by Gender</h2>
                                <div className="d-flex align-items-center justify-content-center">
                                    <PieChart width={300} height={250}>
                                        <Pie
                                            data={genderData}
                                            innerRadius={60}
                                            outerRadius={80}
                                            paddingAngle={5}
                                            dataKey="value"
                                            labelLine={false}
                                        >
                                            <Label
                                                value={totalValue}
                                                position="center"
                                                fontSize="24px"
                                                fontWeight="bold"
                                                fontFamily="italic"
                                                fill="#333"
                                            />
                                            {genderData.map((entry, index) => (
                                                <Cell key={index} fill={entry.color} />
                                            ))}
                                        </Pie>
                                    </PieChart>
                                </div>
                                <div className="flex justify-center mt-4 space-x-4">
                                    {genderData.map((entry, index) => (
                                        <div key={index} className="flex items-center">
                                            <div
                                                className="w-3 h-3 rounded-full mr-2"
                                                style={{ backgroundColor: entry.color }}
                                            ></div>
                                            <span>{entry.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card className="h-100 shadow-lg">
                            <Card.Body>
                                <h2 className="text-md font-bold mb-4">Weekly Attendance</h2>
                                <BarChart width={300} height={250} data={attendanceData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="present" fill="#0088FE" />
                                    <Bar dataKey="absent" fill="#FF8042" />
                                </BarChart>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Event Calendar and Grade Distribution */}
                <Row className="g-4">
                    <Col xs={12} md={6}>
                        <Card className="h-100 shadow-lg">
                            <Card.Body>
                                <h2 className="text-xl font-bold mb-4">Upcoming Events</h2>
                                {upcomingEvents.map((event) => (
                                    <div key={event.id} className="flex items-start mb-3">
                                        <Calendar className="w-5 h-5 text-blue-500 mr-3 mt-1" />
                                        <div>
                                            <p className="font-semibold">{event.title}</p>
                                            <p className="text-sm text-gray-600">{event.date}</p>
                                        </div>
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card className="h-100 shadow-lg">
                            <Card.Body>
                                <h2 className="text-xl font-bold mb-4">Grade Distribution</h2>
                                <PieChart width={300} height={250}>
                                    <Pie
                                        data={gradeDistribution}
                                        cx={150}
                                        cy={125}
                                        innerRadius={60}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {gradeDistribution.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Pending Assignments and Incidents */}
                <Row className="g-4">
                    <Col xs={12} md={6}>
                        <Card className="h-100 shadow-lg">
                            <Card.Body>
                                <h2 className="text-xl font-bold mb-4">Pending Assignments</h2>
                                {assignments.map((assignment) => (
                                    <div key={assignment.id} className="flex items-start mb-3">
                                        <Clock className="w-5 h-5 text-red-500 mr-3 mt-1" />
                                        <div>
                                            <p className="font-semibold">{assignment.title}</p>
                                            <p className="text-sm text-gray-600">{assignment.subject}</p>
                                            <p className="text-sm text-red-500">Due in {assignment.dueDate}</p>
                                        </div>
                                    </div>
                                ))}
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={12} md={6}>
                        <Card className="h-100 shadow-lg">
                            <Card.Body>
                                <h2 className="text-md font-bold mb-4">Incidents and Reports Overview</h2>
                                <div className="flex flex-col items-start space-y-4">
                                    <div>
                                        <h3 className="text-md font-semibold">Total Incidents</h3>
                                        <p className="text-2xl font-bold text-blue-500">45</p>
                                    </div>
                                    <div>
                                        <h3 className="text-md font-semibold">Resolved</h3>
                                        <p className="text-2xl font-bold text-green-500">30</p>
                                    </div>
                                    <div>
                                        <h3 className="text-md font-semibold">Pending</h3>
                                        <p className="text-2xl font-bold text-red-500">15</p>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Change Password Modal */}
                <ModalForm
                    show={showModal}
                    onClose={handleCloseModal}
                    title="Change Password"
                    description="Your password must be at least 8 characters and should include combinations of numbers, letters, and special characters(!$@%)."
                    fields={[
                        { label: "Current Password", name: "currentPassword", type: "password", required: true, placeholder: "Enter current password", pattern: "^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$" },
                        { label: "New Password", name: "newPassword", type: "password", required: true, pattern: "^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$", placeholder: "Enter new password" },
                        { label: "Confirm New Password", name: "confirmNewPassword", type: "password", required: true, pattern: "^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$", placeholder: "Retype new password" },
                    ]}
                    onSubmit={handleFormSubmit}
                    buttonText="Change Password"
                />
            </div>
        </>
    );
};

export default AdminDashboard;